/**
 * Client-side logging.
 *
 * The game runs on the player's machine, inside somebody else's page. When
 * something breaks there, nobody is watching a terminal — the failure shows
 * up as a support ticket saying "the game did not load". This module is the
 * place where that class of problem becomes visible.
 *
 * Three properties shape it:
 *
 *   - **one sink, swappable.** Everything goes through `setLogSink`, which
 *     defaults to the console. Shipping records to a collector later is a
 *     change here, not an edit in thirty call sites;
 *   - **records, not strings.** A log line is `{level, event, error,
 *     context}`. `event` is a stable identifier (`spin.failed`) that
 *     survives message rewording, which is what makes the records
 *     groupable once they leave the console;
 *   - **redacted by construction.** Tokens are bearer credentials, and this
 *     is the one place in the client that deliberately writes state
 *     somewhere it can be read later. See `redact`.
 *
 * What NOT to use it for: telling the player something. That is
 * `state.error`, which the UI renders and the operator receives over
 * postMessage. A log is for whoever has to fix the thing afterwards.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface LogRecord {
  level: LogLevel
  /** Stable identifier, dot-separated: `spin.failed`, `bridge.origin-rejected`. */
  event: string
  /** Human sentence. May be reworded freely — `event` is the stable key. */
  message?: string
  /** The thrown value, normalised for transport. */
  error?: { name: string; message: string; stack?: string }
  /** Anything that helps reproduce it. Redacted before it reaches the sink. */
  context?: Record<string, unknown>
  /** Epoch ms. */
  time: number
}

export type LogSink = (record: LogRecord) => void

const ORDER: Record<LogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40 }

/**
 * Verbosity.
 *
 * Chatty in a dev build, quiet in a production one: a shipped bundle that
 * narrates every theme fetch buries the one line that mattered, and the
 * console of a game inside an operator's page is shared with the operator's
 * own output.
 */
let threshold: LogLevel = import.meta.env.DEV ? 'debug' : 'warn'

export function setLogLevel(level: LogLevel): void {
  threshold = level
}

// ── Redaction ────────────────────────────────────────────────────────────

/**
 * Keys whose value never reaches a log.
 *
 * A launch token is single-use but a refresh token is a live session, and
 * "it is only the console" stops being true the moment this module gains a
 * remote sink — which is the whole reason the sink is swappable. Matching
 * is on a lowercased substring, so `refreshToken`, `X-Operator-Key` and
 * `authorization` are all covered.
 */
const SECRET_KEYS = ['token', 'secret', 'password', 'authorization', 'key', 'credential']

const REDACTED = '[redacted]'

function isSecretKey(key: string): boolean {
  const lower = key.toLowerCase()
  return SECRET_KEYS.some((needle) => lower.includes(needle))
}

/**
 * Strips a token out of anything URL-shaped.
 *
 * The launch token travels in the URL, so it turns up inside a request
 * path, a referrer and a thrown message without anyone putting it there on
 * purpose.
 */
function redactUrlish(value: string): string {
  return value.replace(
    /([?&](?:[a-z_-]*token|key|secret)=)[^&\s]+/gi,
    (_match, prefix: string) => `${prefix}${REDACTED}`,
  )
}

/**
 * Deep-copies a value, dropping secrets and anything that cannot usefully
 * be serialised.
 *
 * The depth limit is not paranoia: a context object can easily end up
 * holding a DOM node or a Pixi display object, and walking one of those
 * fully would cost more than the failure being reported.
 */
function redact(value: unknown, depth = 0): unknown {
  if (depth > 4) return '[deep]'
  if (value === null || value === undefined) return value
  if (typeof value === 'string') return redactUrlish(value)
  if (typeof value === 'number' || typeof value === 'boolean') return value
  if (typeof value === 'bigint') return value.toString()
  if (typeof value === 'function' || typeof value === 'symbol') return `[${typeof value}]`
  if (value instanceof Error) return normaliseError(value)
  if (Array.isArray(value)) {
    // A whole reel strip or a 100-line payout table adds nothing a
    // maintainer can act on, and it is exactly what gets attached by
    // accident.
    const head = value.slice(0, 20).map((item) => redact(item, depth + 1))
    return value.length > 20 ? [...head, `[+${value.length - 20} more]`] : head
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      out[key] = isSecretKey(key) ? REDACTED : redact(item, depth + 1)
    }
    return out
  }
  return String(value)
}

/**
 * Any thrown value into the same three fields.
 *
 * JavaScript lets you throw a string, and a logger that assumes `Error`
 * loses precisely the weird failure that is worth reading about.
 */
function normaliseError(err: unknown): LogRecord['error'] {
  if (err === undefined) return undefined
  if (err instanceof Error) {
    return {
      name: err.name,
      message: redactUrlish(err.message),
      stack: err.stack ? redactUrlish(err.stack) : undefined,
    }
  }
  return { name: 'Thrown', message: redactUrlish(String(err)) }
}

// ── Sink ─────────────────────────────────────────────────────────────────

/**
 * The console sink.
 *
 * `console.error` and friends rather than one channel, so the browser's own
 * level filter keeps working and an error still gets its stack grouping in
 * DevTools.
 */
export const consoleSink: LogSink = (record) => {
  const method =
    record.level === 'error'
      ? console.error
      : record.level === 'warn'
        ? console.warn
        : record.level === 'info'
          ? console.info
          : console.debug

  const head = `[slot] ${record.event}${record.message ? ` — ${record.message}` : ''}`
  const detail: unknown[] = []
  if (record.context && Object.keys(record.context).length > 0) detail.push(record.context)
  if (record.error) {
    detail.push(record.error.stack ?? `${record.error.name}: ${record.error.message}`)
  }

  method(head, ...detail)
}

let sink: LogSink = consoleSink

/**
 * Replaces the destination — a collector, a test spy, a noop.
 *
 * Returns the previous sink so a caller can chain rather than replace, which
 * is what a remote collector wants: keep the console, add the network.
 */
export function setLogSink(next: LogSink): LogSink {
  const previous = sink
  sink = next
  return previous
}

// ── Flood control ────────────────────────────────────────────────────────

/**
 * A repeating failure is one fact, not five hundred.
 *
 * The animation loop and the autospin loop can both fail every frame or
 * every round, and an unthrottled logger turns that into a console nobody
 * can read — and, once there is a remote sink, into a self-inflicted denial
 * of service. The suppressed count is reported when the window rolls over,
 * so the information is compressed rather than lost.
 */
const FLOOD_WINDOW_MS = 10_000
const FLOOD_LIMIT = 5

const flood = new Map<string, { count: number; windowStart: number }>()

/**
 * How many records were dropped since the last one that got through, or
 * null when this one must be dropped too.
 */
function throttle(event: string, now: number): number | null {
  const state = flood.get(event)
  if (!state || now - state.windowStart > FLOOD_WINDOW_MS) {
    flood.set(event, { count: 1, windowStart: now })
    return state && state.count > FLOOD_LIMIT ? state.count - FLOOD_LIMIT : 0
  }
  state.count++
  return state.count > FLOOD_LIMIT ? null : 0
}

// ── API ──────────────────────────────────────────────────────────────────

function emit(
  level: LogLevel,
  event: string,
  message?: string,
  err?: unknown,
  context?: Record<string, unknown>,
): void {
  if (ORDER[level] < ORDER[threshold]) return

  const now = Date.now()
  const suppressed = throttle(event, now)
  if (suppressed === null) return

  const redactedContext = context ? (redact(context) as Record<string, unknown>) : undefined

  const record: LogRecord = {
    level,
    event,
    message,
    error: normaliseError(err),
    context:
      suppressed > 0
        ? { ...redactedContext, suppressedSinceLastReport: suppressed }
        : redactedContext,
    time: now,
  }

  try {
    sink(record)
  } catch {
    // A logger that throws takes down the thing it was reporting on. There
    // is nowhere left to report this to, so it stops here.
  }
}

export const log = {
  debug: (event: string, message?: string, context?: Record<string, unknown>): void =>
    emit('debug', event, message, undefined, context),
  info: (event: string, message?: string, context?: Record<string, unknown>): void =>
    emit('info', event, message, undefined, context),
  warn: (event: string, message?: string, context?: Record<string, unknown>): void =>
    emit('warn', event, message, undefined, context),
  /**
   * `err` comes before the message and is separate from `context` because it
   * is the field a collector groups on — losing a stack into a generic bag
   * is how a hundred distinct crashes become one unreadable bucket.
   */
  error: (
    event: string,
    err: unknown,
    message?: string,
    context?: Record<string, unknown>,
  ): void => emit('error', event, message, err, context),
}

// ── Global handlers ──────────────────────────────────────────────────────

let installed = false

/**
 * Catches what never reaches a `try`.
 *
 * A render-loop throw and a floating promise rejection are exactly the
 * failures nobody wrote a handler for, and by definition the ones the code
 * did not expect. Without this they land in the console of a machine we
 * will never see.
 *
 * Nothing is prevented here — `preventDefault` is deliberately not called,
 * so the browser still reports the error the way it normally would.
 */
export function installGlobalErrorHandlers(): void {
  if (installed) return
  installed = true

  window.addEventListener('error', (e) => {
    log.error('window.error', e.error ?? e.message, 'Uncaught error', {
      source: e.filename,
      line: e.lineno,
      column: e.colno,
    })
  })

  window.addEventListener('unhandledrejection', (e) => {
    log.error('window.unhandled-rejection', e.reason, 'Promise rejected with no handler')
  })
}
